/**
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableEntryParams:
 *      type: object
 *      properties:
 *        ChampionshipResultTableId:
 *          description: ID of the associated ChampionshipResultTable
 *          type: string
 *        RegattaEntryId:
 *          description: ID of the associated RegattaEntry
 *          type: string
 *        NameOverride:
 *          description: NameOverride of the ChampionshipResultTableEntry
 *          type: string
 *        Place:
 *          description: Place of the ChampionshipResultTableEntry
 *          type: string
 *        Points:
 *          description: Points of the ChampionshipResultTableEntry
 *          type: integer
 *
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableEntryCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipResultTableEntryParams'
 *        - type: object
 *          required:
 *            - ChampionshipResultTableId
 *            - RegattaEntryId
 *            - Points
 *
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableEntryQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the ChampionshipResultTableEntry
 *              type: string
 *        - $ref: '#/definitions/ChampionshipResultTableEntryParams'
 *
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableEntryUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipResultTableEntryQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    ChampionshipResultTableEntry:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipResultTableEntryCreateParams'
 *        - $ref: '#/definitions/ChampionshipResultTableEntryUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      ChampionshipResultTableEntry:
 *        allOf:
 *          - $ref: '#/definitions/ChampionshipResultTableEntry'
 *          - type: object
 *            properties:
 *              ChampionshipResultTable:
 *                description: ChampionshipResultTable of the ChampionshipResultTableEntry
 *                $ref: '#/components/schemas/ChampionshipResultTable'
 *              RegattaEntry:
 *                description: RegattaEntry of the ChampionshipResultTableEntry
 *                $ref: '#/components/schemas/RegattaEntry'
 *              ChampionshipResultTableRaceResults:
 *                description: |
 *                  ChampionshipResultTableRaceResults of the ChampionshipResultTableEntry
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/ChampionshipResultTableRaceResult'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  ChampionshipResultTableId: string;
  RegattaEntryId: string;
};

export type $Params = {
  NameOverride: null | string;
  Place: null | string;
  Points: number;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  ChampionshipResultTable?: $Model<'ChampionshipResultTable'>;
  ChampionshipResultTableRaceResults?: Array<$Model<'ChampionshipResultTableRaceResult'>>;
  RegattaEntry?: $Model<'RegattaEntry'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  NameOverride: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Place: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Points: {
    allowNull: false,
    type: Sequelize.DOUBLE(
      4,
      1,
    ),
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'ChampionshipResultTableEntry'> => {
  const ChampionshipResultTableEntry = stack.define(
    'ChampionshipResultTableEntry',
    fields(Sequelize),
    {
      tableName: 'ChampionshipResultTableEntries',
    },
  );

  ChampionshipResultTableEntry.associate = (models: $ModelClasses) => {
    models.ChampionshipResultTableEntry.belongsTo(
      models.ChampionshipResultTable,
      {
        as: 'ChampionshipResultTable',
        foreignKey: 'ChampionshipResultTableId',
      },
    );
    models.ChampionshipResultTableEntry.belongsTo(
      models.RegattaEntry,
      {
        as: 'RegattaEntry',
        foreignKey: 'RegattaEntryId',
      },
    );
    models.ChampionshipResultTableEntry.hasMany(
      models.ChampionshipResultTableRaceResult,
      {
        as: 'ChampionshipResultTableRaceResults',
        foreignKey: 'ChampionshipResultTableEntryId',
      },
    );
  };

  ChampionshipResultTableEntry.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return ChampionshipResultTableEntry;
});
